/**
 * Formato numérico del tablero. **2026-07-28 (ronda 3, decisión del usuario):**
 * formato MEXICANO en TODO el tablero — coma de miles y punto decimal
 * (`es-MX`): `1,234,567` / `71.8%` / `$1,234,567`. Antes se usaba `de-DE`
 * (`1.234.567` / `71,8%`) por el handoff; el usuario pidió el formato mexicano.
 */

/**
 * Entero con separador de miles estilo `198.827`. `null`/`undefined`/`NaN`
 * -> `'—'` (los conteos de la API casi siempre son enteros no-nulos, pero
 * `paresVendidos`/`tiempoPromedio` sí pueden llegar `null` cuando Proscai no
 * respondió o no hay datos).
 */
export function fmtInt(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) {
    return '—';
  }
  return Math.round(n).toLocaleString('es-MX');
}

/**
 * Monto $ (MXN) tipo `$1.234.567` — mismo estilo `de-DE` del tablero (punto
 * de miles), SIN decimales. `null`/`undefined`/`NaN` -> `'—'`. **Nuevo
 * 2026-07-28** (ajuste "montos $ en todos lados"). El monto es el bruto del
 * ticket (`FDOC.DBRUTO`) contado una sola vez por documento.
 */
export function fmtMoney(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) {
    return '—';
  }
  // `Math.round(-0.3)` es -0 y `toLocaleString` lo pinta «-0»: el 12-oct salió
  // «$-0» en el descuento de una tienda con centavos negativos. Cero es cero.
  const redondeado = Math.round(n) || 0;
  return '$' + redondeado.toLocaleString('es-MX');
}

/**
 * Fracción 0..1 (como llegan `pctEfectividad`/`pctSla`/`pctDispPiso`/
 * `pctTasa` desde la API) a un string tipo `71,8%` (coma decimal). `null`/
 * `undefined`/`NaN` -> `'—'`.
 */
export function fmtPct(
  fraccion: number | null | undefined,
  dec: number = 1,
): string {
  if (fraccion === null || fraccion === undefined || Number.isNaN(fraccion)) {
    return '—';
  }
  // Mismo cuidado que `fmtMoney`: un -0,04 % redondeado a un decimal es «-0,0 %».
  const escala = 10 ** dec;
  const pct = Math.round(fraccion * 100 * escala) / escala || 0;
  return (
    pct.toLocaleString('es-MX', {
      minimumFractionDigits: dec,
      maximumFractionDigits: dec,
    }) + '%'
  );
}

/**
 * Segundos -> `mm:ss` con padding (ej. `130` -> `'02:10'`). `null`/
 * `undefined`/`NaN` -> `'—'` (ej. `tiempoPromedio`/`tiempoPromSeg` sin dato).
 */
export function mmss(seg: number | null | undefined): string {
  if (seg === null || seg === undefined || Number.isNaN(seg)) {
    return '—';
  }
  const m = Math.floor(seg / 60);
  const s = Math.round(seg % 60);
  return (m < 10 ? '0' : '') + m + ':' + (s < 10 ? '0' : '') + s;
}

/**
 * Texto de la celda/etapa "Venta (pares)" cuando Proscai no respondió en el
 * fetch (HF Fix 3): el primer `GET /dashboard/sucursales` de un rango nuevo
 * puede llegar con TODOS los `paresVendidos` en `null` porque la Venta se
 * cachea con un timeout de 8s y el caché aún está frío — `IndicadoresScreen`
 * dispara UN reintento automático ~15s después para recoger la Venta ya
 * calentada. Mientras ese reintento sigue pendiente (`ventaCalculando`
 * true) se muestra `"calculando…"` en vez de `"N/D"`, para no leerse como
 * "no hay venta" cuando en realidad solo está tardando. Si el reintento
 * también vuelve sin datos, `ventaCalculando` pasa a `false` (se agotó el
 * único intento) y aquí SÍ se rinde con `"N/D"`.
 */
export function fmtVenta(
  pares: number | null | undefined,
  ventaUnavailable: boolean,
  ventaCalculando: boolean,
): string {
  if (ventaUnavailable) {
    return ventaCalculando ? 'calculando…' : 'N/D';
  }
  return fmtInt(pares);
}

/**
 * `stroke-dasharray` para los anillos SVG de progreso: `pct` en 0..100 y
 * `circ` la circunferencia del círculo (`2 * PI * r`, distinta según el
 * tamaño del anillo — 213.628 para r=34, 163.363 para r=26 en el handoff).
 * Recorta `pct` a [0,100] para no romper el trazo con datos fuera de rango.
 */
export function ring(pct: number | null | undefined, circ: number): string {
  const safePct = pct === null || pct === undefined || Number.isNaN(pct) ? 0 : pct;
  const clamped = Math.min(Math.max(safePct, 0), 100);
  return `${((clamped / 100) * circ).toFixed(1)} ${circ.toFixed(1)}`;
}

/**
 * Color semáforo para "tasa de atención" (venta ÷ solicitudes ATENDIDAS, V ÷ A
 * desde el 12-oct en todas las superficies), recibe el porcentaje YA en escala
 * 0..100 (no fracción) — puede superar 100 (incluso miles) cuando hay venta de
 * piso fuera del sistema. Se usa a través de `semaforo('tasa', …)` del glosario.
 *
 * Semántica invertida a propósito respecto de `slaColor`:
 * idealmente TODA la venta de la tienda pasa por la herramienta, es decir
 * `Atendidas >= Venta`, así que Tasa <= 100% es lo esperado/sano. Un valor
 * ALTO significa que la tienda vendió más de lo que solicitó al sistema —
 * venta fuera de la herramienta — por lo que ALTO es MALO (no cumplimiento) y
 * BAJO/<=100% es BUENO. Umbrales (decisión de producto 2026-07-20):
 * <=100 verde (sano), <=200 dorado intermedio (algo de venta fuera del
 * sistema), >200 rojo (la tienda casi no usa el sistema).
 */
export function tasaColor(pct0a100: number | null | undefined): string {
  if (pct0a100 === null || pct0a100 === undefined || Number.isNaN(pct0a100)) {
    return '#9298a3';
  }
  if (pct0a100 <= 100) return '#17a44d';
  if (pct0a100 <= 200) return '#b7891f';
  return '#d63a33';
}

/**
 * Color semáforo de «Atendidas por bodeguero» (A ÷ S), recibe el porcentaje YA
 * en escala 0..100. Umbrales: >=95 verde, >=90 dorado intermedio, si no rojo.
 *
 * ⚠️ Es el ÚNICO juego de umbrales de A ÷ S (12-oct). Hasta entonces la tarjeta
 * «Bodega» de Mi tienda pintaba la MISMA cifra con 93/88 (`bodegaSlaColor`,
 * retirado) y el medidor de Global con verde fijo. Se usa a través de
 * `semaforo('atendidasBodega', …)` del glosario.
 */
export function slaColor(pct0a100: number | null | undefined): string {
  if (pct0a100 === null || pct0a100 === undefined || Number.isNaN(pct0a100)) {
    return '#9298a3';
  }
  if (pct0a100 >= 95) return '#17a44d';
  if (pct0a100 >= 90) return '#b7891f';
  return '#d63a33';
}

/**
 * Concordancia de número: «1 par», «2 pares», «1 tienda activa». Se pasa la
 * cifra YA formateada aparte; aquí sólo se elige la palabra. `n` se compara
 * redondeado, igual que lo pinta `fmtInt`.
 */
export function plural(n: number | null | undefined, singular: string, pluralForma: string): string {
  return Math.round(n ?? 0) === 1 ? singular : pluralForma;
}

/** `fmtInt(n)` + la palabra concordada: «1 par», «1,234 pares». */
export function fmtCuenta(n: number | null | undefined, singular: string, pluralForma: string): string {
  return `${fmtInt(n)} ${plural(n, singular, pluralForma)}`;
}
